import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { alphabetical } from './order';
import { TaskQuestions } from './QuestionsPanel';
import { Modal, TabPanels } from '@core/ui/Modal';
import { ObjectHistory } from '../../history/ObjectHistory';
import { SegmentedControl } from '@core/ui/SegmentedControl';
import { Button } from '@core/ui/Button';
import { DeleteConfirm, FormError, ModalFooter, SAVE_CONFLICT, fieldInput as input, fieldLabel as label, readOnlyFields, submitOnCtrlEnter } from '@core/ui/form';
import { confirmDialog } from '@core/ui/confirm';
import { data, useData, useSetting, useTable } from '@core/store';
import { AssigneePicker, usePeopleList, useTeamList } from './AssigneePicker';
import { PeriodField } from './PeriodField';
import { EntitySelect } from '@core/ui/EntitySelect';
import { mondayOf } from '@core/dates';
import { actorLabel } from '@core/people';
import { useAuth } from '@core/auth/store';
import { useCanWrite } from '@core/auth/access';
import { planDuplicate } from './planner';
import { DEPLOY_TOO_EARLY, deployTooEarly } from './deployGuard';
import { MAX_ASSIGNEES, rowKeyOf, type Task } from './data';
import { diffFields, draftFromTask, planTaskSave, type CardTab, type Draft, type Field } from './cardEdits';


const FORM_ID = 'task-form'; // Save живёт в футере окна, вне формы

const SIDE = 'flex flex-col gap-1 text-sm font-medium'; // колонка свойств: мельче основной
const SIDE_INPUT = `${input} py-1.5 text-sm`;

const FIELD_LABELS: Record<Field, string> = {
  title: 'title', project: 'project', dates: 'dates', assignees: 'assignees',
  design_status: 'design', deploy: 'deploy', release: 'release marks', notes: 'notes', in_backlog: 'backlog',
};

/**
 * Карточка задачи. Правка живёт как набор тронутых полей поверх живой строки: что не трогал —
 * обновляется на месте, когда меняет другой; что трогал — твоё, и только оно записывается.
 * Сохранение с expect_updated_at живой строки: если её изменили между чтением и записью —
 * conflict, а не тихая перезапись.
 */
export function TaskModal({ draft, allTasks, onClose, initialTab = 'task' }: { draft: Draft; allTasks: Map<string, Task>; onClose: () => void; initialTab?: CardTab }) {
  const canWrite = useCanWrite();
  const [tab, setTab] = useState<CardTab>(draft.id ? initialTab : 'task');
  const questions = useTable('question');
  const openQuestions = useMemo(() => [...questions.values()].filter((q) => q.task_id === draft.id && q.status === 'open').length, [questions, draft.id]);
  const projects = useTable('project');
  const subprojects = useTable('subproject');
  const people = useTable('person');
  const profiles = useTable('profile');
  const assigneeRows = useTable('task_assignee');
  const [edits, setEdits] = useState<Partial<Draft>>({});
  const [touched, setTouched] = useState<Set<Field>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const live = draft.id ? allTasks.get(draft.id) : undefined;
  const deleted = !!draft.id && !live;
  const liveAssignees = useMemo(
    () => (draft.id ? [...assigneeRows.values()].filter((a) => a.task_id === draft.id).sort((a, b) => a.position - b.position).map((a) => a.person_id) : []),
    [assigneeRows, draft.id],
  );
  const base: Draft = useMemo(() => (live ? draftFromTask(live, liveAssignees) : draft), [live, liveAssignees, draft]);
  // кто последним трогал назначения: у task_assignee свой updated_by, у строки задачи он при этом не меняется
  const assigneeActor = useMemo(() => {
    let last: { updated_at: string; updated_by: string | null } | null = null;
    for (const a of assigneeRows.values()) if (a.task_id === draft.id && (!last || a.updated_at > last.updated_at)) last = a;
    return last?.updated_by ?? null;
  }, [assigneeRows, draft.id]);
  const d: Draft = { ...base, ...edits };

  // Чужие правки за время, пока карточка открыта, — накопительно: каждое изменение живой
  // строки сравнивается с предыдущим и дописывается в список. Дифф «сейчас против открытия»
  // терял бы правку, если следующая вернула поле к исходному.
  const me = useAuth((s) => s.profile?.id);
  const prevBase = useRef<Draft>(base);
  const [foreign, setForeign] = useState<{ fields: Field[]; by: string; at: number }[]>([]);
  useEffect(() => {
    const was = prevBase.current;
    const now = base;
    prevBase.current = base;
    if (!live) return;
    const diff = diffFields(was, now);
    if (diff.length === 0) return;
    const actor = diff.every((f) => f === 'assignees') ? assigneeActor : live.updated_by;
    if (actor === me) return;
    const by = actorLabel(actor, profiles, people).name;
    setForeign((prev) => [...prev, { fields: diff, by, at: Date.now() }]);
  }, [base, live, me, profiles, people, assigneeActor]);
  const changedByOthers = useMemo(() => [...new Set(foreign.flatMap((f) => f.fields))], [foreign]);
  const changedBy = useMemo(() => [...new Set(foreign.map((f) => f.by))], [foreign]);

  // Правка «сделал и откатил» — не правка: тронутые поля, чьё значение отличается от живого.
  const changed = diffFields(base, d);
  const dirty = new Set<Field>([...touched].filter((f) => changed.includes(f)));

  // Кто ещё держит эту карточку открытой прямо сейчас — из присутствия.
  const presence = useData((s) => s.presence);
  const alsoEditing = useMemo(
    // у нового черновика id нет, а у всех, кто ничего не редактирует, editing = null — не путать
    () => (draft.id ? Object.values(presence).filter((p) => p.profile_id !== me && p.editing === draft.id).map((p) => p.name) : []),
    [presence, me, draft.id],
  );

  const projectList = useMemo(() => alphabetical(projects.values()), [projects]);
  const subList = useMemo(() => [...subprojects.values()].filter((s) => s.project_id === d.project_id).sort((a, b) => a.position - b.position), [subprojects, d.project_id]);
  const firstSubOf = (projectId: string | null) => [...subprojects.values()].filter((s) => s.project_id === projectId).sort((a, b) => a.position - b.position)[0]?.id ?? null;
  // у проекта с подпроектами задача живёт в подпроекте: пустой выбор подставляет верхний (и помечает поле)
  useEffect(() => {
    if (canWrite && subList.length > 0 && !d.subproject_id) touch('project', { subproject_id: subList[0]!.id });
  });
  const peopleList = usePeopleList();
  const teamList = useTeamList();
  const [assigneeGrid, setAssigneeGrid] = useSetting<boolean>('card.assignee_grid', false); // решётка всех людей — по желанию, помнится
  const rowTasks = useMemo(
    () => [...allTasks.values()].filter((t) => rowKeyOf(t.project_id, t.subproject_id) === rowKeyOf(d.project_id, d.subproject_id)),
    [allTasks, d.project_id, d.subproject_id],
  );

  const touch = (f: Field, patch: Partial<Draft>) => {
    setEdits((prev) => ({ ...prev, ...patch }));
    setTouched((prev) => new Set(prev).add(f));
  };

  async function save(e: FormEvent) {
    e.preventDefault();
    if (!d.title.trim() || !d.project_id) { setError('Title and project are required'); return; }
    if (d.deploy === 'date' && deployTooEarly(d.deploy_date, d.end_date)) { setError(DEPLOY_TOO_EARLY); return; }
    if (d.id && !live) { setError('This task was deleted'); return; }
    setBusy(true); setError(null);
    const { taskId, changes } = planTaskSave(d, dirty, live, [...assigneeRows.values()], rowTasks, () => crypto.randomUUID());
    if (changes.length === 0) { setBusy(false); onClose(); return; }
    const r = await data.applyChanges(changes, { kind: d.id ? 'edit_task' : 'create_task', task_id: taskId });
    setBusy(false);
    if (!r.ok) { setError(r.conflict ? SAVE_CONFLICT : r.error); return; }
    onClose();
  }

  async function remove() {
    if (!d.id) return;
    setBusy(true);
    const r = await data.applyChanges([{ table: 'task', id: d.id, delete: true }], { kind: 'delete_task', task_id: d.id });
    setBusy(false);
    if (!r.ok) { setError(r.error); return; }
    onClose();
  }

  async function duplicate() {
    if (!live) return;
    setBusy(true);
    const newId = crypto.randomUUID();
    const r = await data.applyChanges(planDuplicate(live, [...assigneeRows.values()], rowTasks, newId), { kind: 'duplicate_task', task_id: live.id, new_task_id: newId });
    setBusy(false);
    if (!r.ok) { setError(r.error); return; }
    onClose();
  }

  // Закрытие с несохранёнными правками (Escape, клик вне, Cancel) — спросить; Save и Delete закрывают сами.
  const close = async () => {
    if (canWrite && !deleted && dirty.size > 0) {
      const ok = await confirmDialog({ title: 'Discard changes?', text: <>The task has unsaved changes: <b>{[...dirty].map((f) => FIELD_LABELS[f]).join(', ')}</b>.</>, yes: 'Discard', danger: true });
      if (!ok) return;
    }
    onClose();
  };

  const mark = (f: Field) => {
    if (!changedByOthers.includes(f) || touched.has(f)) return null;
    const last = [...foreign].reverse().find((x) => x.fields.includes(f));
    return <span className="ml-2 text-sm font-normal text-accent">updated by {last?.by}</span>;
  };

  const head = (
    <>
      {deleted && (
        <div role="alert" className="mb-4 rounded-md bg-red-50 border border-red-300 text-danger px-3 py-2 font-medium">
          This task was deleted by someone else. <Button variant="ghost" className="ml-2" onClick={onClose}>Close</Button>
        </div>
      )}
      {!deleted && alsoEditing.length > 0 && (
        <div role="alert" className="mb-4 rounded-md bg-red-50 border border-red-300 text-danger px-3 py-2 font-medium">
          {alsoEditing.join(', ')} {alsoEditing.length > 1 ? 'are' : 'is'} editing this task right now.
        </div>
      )}
      {!deleted && foreign.length > 0 && (
        <div role="status" className="mb-4 rounded-md bg-blue-50 border border-blue-300 text-accent-strong px-3 py-2">
          <span className="font-medium">{changedBy.join(' and ')} changed {changedByOthers.map((x) => FIELD_LABELS[x]).join(', ')}</span> while you were editing.
          <span className="text-sm"> Fields you haven't touched show the new values.</span>
        </div>
      )}
      {/* у новой задачи вопросов быть не может — вкладок нет вовсе, чтобы не путать */}
      {d.id && (
        <SegmentedControl
          role="tabs" label="Task card" className="mb-4" value={tab} onChange={setTab}
          items={[{ key: 'task', label: 'Task' }, { key: 'questions', label: 'Questions', count: openQuestions || undefined, title: openQuestions ? `${openQuestions} open` : undefined }, { key: 'history', label: 'History' }]}
        />
      )}
    </>
  );
  const footer = (
    <ModalFooter form={FORM_ID} busy={busy} onCancel={() => void close()} readOnly={!canWrite} saveDisabled={deleted || (!!d.id && dirty.size === 0)} left={d.id && (
      <DeleteConfirm label="Delete" question="Delete this task?" busy={busy} disabled={deleted} onDelete={() => void remove()}>
        <Button variant="secondary" onClick={() => void duplicate()} disabled={busy || deleted}>Duplicate</Button>
      </DeleteConfirm>
    )} />
  );
  // обе панели смонтированы (TabPanels): вопросы занимают ту же высоту, что форма, окно не прыгает
  // две колонки: слева то, без чего задачи нет (название, место, период, исполнители, заметки), справа —
  // колонка свойств: дизайн, выход, метки релиза, бэклог, пин. На узком экране колонка уходит под основную.
  const form = (
      <form id={FORM_ID} onSubmit={(e) => void save(e)} onKeyDown={canWrite ? submitOnCtrlEnter : undefined} className="grid gap-4 md:grid-cols-[minmax(0,1fr)_220px]">
        <div className="flex flex-col gap-4 min-w-0">
          <label className={label}>Title{mark('title')}
            <input className={input} value={d.title} onChange={(e) => touch('title', { title: e.target.value })} autoFocus required />
          </label>
          <div className="grid grid-cols-2 gap-4">
            <div className={label}>
              <span>Project{mark('project')}</span>
              <EntitySelect label="Project" value={d.project_id} options={projectList} disabled={!canWrite} onChange={(id) => touch('project', { project_id: id ?? '', subproject_id: firstSubOf(id) })} />
            </div>
            <div className={label}>
              <span>Subproject</span>
              <EntitySelect label="Subproject" value={d.subproject_id} options={subList.map((s) => ({ id: s.id, name: s.name }))} empty={subList.length === 0 ? '—' : undefined} disabled={!canWrite || subList.length === 0} onChange={(id) => touch('project', { subproject_id: id })} />
            </div>
          </div>
          <div className={label}>
            <span>When{mark('dates')}</span>
            <PeriodField start={d.start_date} end={d.end_date} disabled={!canWrite} onChange={(start_date, end_date) => touch('dates', { start_date, end_date })} />
          </div>
          <div className={label}>
            <span>Assignees <span className="text-sm text-gray-500 font-normal">up to {MAX_ASSIGNEES}</span>{mark('assignees')}</span>
            <AssigneePicker value={d.assignees} onChange={(assignees) => touch('assignees', { assignees })} people={peopleList} teams={teamList} grid={{ open: assigneeGrid, onToggle: setAssigneeGrid }} />
          </div>
          <label className={label}>Notes{mark('notes')}
            <textarea className={input} rows={3} value={d.notes} onChange={(e) => touch('notes', { notes: e.target.value })} />
          </label>
          <FormError error={error} />
        </div>

        <aside className="flex flex-col gap-3 rounded-md bg-gray-50 border border-line p-3 self-start" aria-label="Task properties">
          <label className={SIDE}>Design{mark('design_status')}
            <select className={SIDE_INPUT} value={d.design_status} onChange={(e) => touch('design_status', { design_status: e.target.value as Draft['design_status'] })}>
              <option value="not_needed">Not needed</option>
              <option value="needed">Needed</option>
              <option value="done">Done</option>
            </select>
          </label>
          <label className={SIDE}>Deploy{mark('deploy')}
            <select className={SIDE_INPUT} value={d.deploy} onChange={(e) => touch('deploy', { deploy: e.target.value as Draft['deploy'] })}>
              <option value="auto">Last week of task</option>
              <option value="none">Not deploying</option>
              <option value="date">On a date</option>
            </select>
          </label>
          {d.deploy === 'date' && (
            <label className={SIDE}>Deploy date
              <input type="date" className={SIDE_INPUT} value={d.deploy_date} min={mondayOf(d.end_date)} onChange={(e) => e.target.value && touch('deploy', { deploy_date: e.target.value })} />
            </label>
          )}
          {d.deploy === 'date' && deployTooEarly(d.deploy_date, d.end_date) && (
            <p className="text-sm text-danger font-medium" role="alert" data-deploy-warning>{DEPLOY_TOO_EARLY}.</p>
          )}
          {/* внешние метки — их смотрит заказчик на Release Plan; на барах — глифы в названии */}
          <label className="flex items-center gap-2 text-sm font-medium">
            <input type="checkbox" className="w-5 h-5" checked={d.key_release} onChange={(e) => touch('release', { key_release: e.target.checked })} />
            Key release ★{mark('release')}
          </label>
          {/* уверенность — два флага, взаимоисключающие: снятие одного не ставит другой */}
          <label className="flex items-center gap-2 text-sm font-medium">
            <input type="checkbox" className="w-5 h-5" checked={d.release_confidence === 'confirmed'} onChange={(e) => touch('release', { release_confidence: e.target.checked ? 'confirmed' : null })} />
            Confirmed ✓
          </label>
          <label className="flex items-center gap-2 text-sm font-medium">
            <input type="checkbox" className="w-5 h-5" checked={d.release_confidence === 'at_risk'} onChange={(e) => touch('release', { release_confidence: e.target.checked ? 'at_risk' : null })} />
            At risk !
          </label>
          <label className="flex items-center gap-2 text-sm font-medium">
            <input type="checkbox" className="w-5 h-5" checked={d.in_backlog} onChange={(e) => touch('in_backlog', { in_backlog: e.target.checked })} />
            In backlog{mark('in_backlog')}
          </label>
        </aside>
      </form>
  );
  return (
    <Modal open onClose={() => void close()} title={d.id ? 'Edit task' : 'New task'} width="max-w-4xl" head={head} footer={footer}>
      {d.id ? <TabPanels active={tab} panels={[['task', readOnlyFields(!canWrite, form)], ['questions', <TaskQuestions taskId={d.id} active={tab === 'questions'} />], ['history', <ObjectHistory table="task" id={d.id} active={tab === 'history'} />]]} /> : readOnlyFields(!canWrite, form)}
    </Modal>
  );
}
